import React, { useCallback } from 'react';

import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState } from '../types';
import {
  createEmptyEntry,
  createEmptyNoteEntry,
  MAX_ENTRIES,
  MAX_NOTE_ENTRIES,
  QuickCheckInState,
} from './use-check-in-form-state';

export interface UseCheckInEntriesOptions {
  setState: React.Dispatch<React.SetStateAction<QuickCheckInState>>;
  setNoteEntries: React.Dispatch<React.SetStateAction<NoteEntryState[]>>;
}

export function useCheckInEntries({ setState, setNoteEntries }: UseCheckInEntriesOptions) {
  const handleUpdateEntry = useCallback(
    (id: string, patch: Partial<CheckInEntryState>) => {
      setState((prev) => ({
        ...prev,
        entries: prev.entries.map((e) =>
          e.id === id ? { ...e, ...patch } : e
        ),
      }));
    },
    [setState]
  );

  const handleRemoveEntry = useCallback((id: string) => {
    setState((prev) => {
      if (prev.entries.length <= 1) return prev;
      return { ...prev, entries: prev.entries.filter((e) => e.id !== id) };
    });
  }, [setState]);

  const handleAddEntry = useCallback(() => {
    setState((prev) => {
      if (prev.entries.length >= MAX_ENTRIES) return prev;
      return { ...prev, entries: [...prev.entries, createEmptyEntry()] };
    });
  }, [setState]);

  const handleUpdateNoteEntry = useCallback(
    (id: string, patch: Partial<NoteEntryState>) => {
      setNoteEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, ...patch } : e))
      );
    },
    [setNoteEntries]
  );

  const handleRemoveNoteEntry = useCallback((id: string) => {
    setNoteEntries((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((e) => e.id !== id);
    });
  }, [setNoteEntries]);

  const handleAddNoteEntry = useCallback(() => {
    setNoteEntries((prev) => {
      if (prev.length >= MAX_NOTE_ENTRIES) return prev;
      return [...prev, createEmptyNoteEntry()];
    });
  }, [setNoteEntries]);

  return {
    handleUpdateEntry,
    handleRemoveEntry,
    handleAddEntry,
    handleUpdateNoteEntry,
    handleRemoveNoteEntry,
    handleAddNoteEntry,
  };
}
